import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../api/client';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm } from '../../../components/confirm';
import { FormTitle } from '../../../components/FormTitle';
import { AppToolbar } from '../../../components/AppToolbar';

import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import type { RequisitionPrefill } from './requisitionPrefill';

interface Option { id: string; name: string; }

interface RequisitionForm {
  itemId: string;
  vendorId: string;
  manufacturerId: string;
  catalog: string;
  orderQty: number;
  unitPrice: number;
  shippingOptionId: string;
  deliveryPriorityId: string;
  departmentId: string;
  projectId: string;
  comment: string;
}

const EMPTY: RequisitionForm = {
  itemId: '', vendorId: '', manufacturerId: '', catalog: '',
  orderQty: 1, unitPrice: 0,
  shippingOptionId: '', deliveryPriorityId: '',
  departmentId: '', projectId: '', comment: '',
};

/** Hand-off shape from a "Requisition" action elsewhere (e.g. Stock Alert) —
 *  mirrors legacy's Requisition_ListViewEntermode auto-add: item, vendor,
 *  manufacturer, unit price, catalog. Passed via router `location.state`.
 *  Legacy ALSO stamps the requester's own Department on that auto-added row
 *  (`RequisitionViewcontroller.cs` ~:332-339); this page fetches it from
 *  `GET /api/v1/requisitions/my-department` when it opens with a prefill, so
 *  the sender does not need to know it. ExpPrice is computed by the save SP. */
/* The prefill payload moved to ./requisitionPrefill when Stock Alert's link was
   pointed at the Item Requisition LIST (legacy's Requisition_ListViewEntermode)
   instead of this page -- both screens needed the type, and neither owns it.
   Re-exported here so an existing import path keeps working. */
export type { RequisitionPrefill } from './requisitionPrefill';

export default function RequisitionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotify();
  const saveFlow = useSaveFlow();
  const { confirm } = useConfirm();

  const prefill = isNew
    ? (location.state as { prefill?: RequisitionPrefill } | null)?.prefill
    : undefined;

  const [form, setForm] = useState<RequisitionForm>(() =>
    prefill
      ? {
          ...EMPTY,
          itemId: prefill.itemId,
          vendorId: prefill.vendorId ?? '',
          manufacturerId: prefill.manufacturerId ?? '',
          catalog: prefill.catalog ?? '',
          unitPrice: prefill.unitPrice ?? 0,
        }
      : EMPTY);
  const [rqId, setRqId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Legacy hides Save for a user with Navigate-but-not-Write on "Requisition"
  // (RequisitionViewcontroller.cs :305-310). Default true so the button is not
  // briefly disabled for everyone while the probe is in flight; the API
  // re-checks on every write, so this is a mirror, never the gate.
  const [canWrite, setCanWrite] = useState(true);

  const [items, setItems] = useState<Option[]>([]);
  const [vendors, setVendors] = useState<Option[]>([]);
  const [manufacturers, setManufacturers] = useState<Option[]>([]);
  const [shippingOptions, setShippingOptions] = useState<Option[]>([]);
  const [deliveryPriorities, setDeliveryPriorities] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [projects, setProjects] = useState<Option[]>([]);

  const loadLookups = useCallback(async () => {
    try {
      const [itRes, vRes, mfgRes, shRes, dpRes, deptRes, prjRes] = await Promise.all([
        api.get('/api/v1/requisitions/items'),
        api.get('/api/v1/vendor-coas/vendor-options'),
        api.get('/api/v1/requisitions/manufacturers'),
        api.get('/api/v1/shipping-options'),
        api.get('/api/v1/delivery-priorities'),
        api.get('/api/v1/system/departments'),
        api.get('/api/v1/icm-projects'),
      ]);
      const toArr = (d: unknown): unknown[] => Array.isArray(d) ? d : ((d as { data?: unknown[] })?.data ?? []);
      const mapOpt = (d: unknown) => (toArr(d) as Array<{
        oid?: string; id?: string;
        itemName?: string; name?: string; vendorName?: string; manufacturerName?: string;
      }>)
        .map(i => ({ id: i.oid ?? i.id ?? '', name: i.itemName ?? i.vendorName ?? i.manufacturerName ?? i.name ?? '' }));
      setItems(mapOpt(itRes.data));
      setVendors(mapOpt(vRes.data));
      setManufacturers(mapOpt(mfgRes.data));
      setShippingOptions(mapOpt(shRes.data));
      setDeliveryPriorities(mapOpt(dpRes.data));
      setDepartments(mapOpt(deptRes.data));
      setProjects(mapOpt(prjRes.data));
    } catch {
      notify.error('Failed to load lookup data.');
    }
  }, [notify]);

  const loadRecord = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/requisitions/${id}`);
      const d = res.data as {
        itemId?: string; vendorId?: string; manufacturerId?: string; catalog?: string;
        orderQty?: number; unitPrice?: number; shippingOptionId?: string; deliveryPriorityId?: string;
        departmentId?: string; projectId?: string; comment?: string; rqid?: string; statusLabel?: string;
      };
      setForm({
        itemId: d.itemId ?? '', vendorId: d.vendorId ?? '', manufacturerId: d.manufacturerId ?? '',
        catalog: d.catalog ?? '', orderQty: d.orderQty ?? 1, unitPrice: d.unitPrice ?? 0,
        shippingOptionId: d.shippingOptionId ?? '', deliveryPriorityId: d.deliveryPriorityId ?? '',
        departmentId: d.departmentId ?? '', projectId: d.projectId ?? '', comment: d.comment ?? '',
      });
      setRqId(d.rqid ?? '');
      setStatus(d.statusLabel ?? '');
    } catch {
      notify.error('Failed to load requisition.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, notify]);

  useEffect(() => {
    void loadLookups();
    void loadRecord();
  }, [loadLookups, loadRecord]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/requisitions/can-write');
        if (!cancelled) setCanWrite((res.data as { canWrite?: boolean })?.canWrite !== false);
      } catch {
        // Probe failed — leave Save enabled and let the API's own 403 decide.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stock Alert → Requisition: default Department to the requester's own
  // (legacy parity, see RequisitionPrefill). Only fills an EMPTY field, so a
  // user who already picked one before the call returns is never overridden.
  useEffect(() => {
    if (!prefill) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/requisitions/my-department');
        const deptId = (res.data as { departmentId?: string | null })?.departmentId;
        if (!cancelled && deptId) setForm(f => (f.departmentId ? f : { ...f, departmentId: deptId }));
      } catch {
        // No department resolvable — the field stays empty and the save-time
        // "Department should not be Empty" validation still applies.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function set<K extends keyof RequisitionForm>(k: K, v: RequisitionForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function handleSave() {
    // EVERY optional Guid field must go as null, never "". An unpicked select
    // holds "", which ASP.NET cannot bind to a Guid? — the request then failed
    // at model binding with a raw framework envelope the form could only show
    // as "Save failed.". departmentId and deliveryPriorityId were missing from
    // this list, so a save with either of them blank never reached the server's
    // rules at all and the user was never told which field to fix (found in
    // browser verification, 2026-09-29).
    const payload = {
      ...form,
      vendorId: form.vendorId || null,
      manufacturerId: form.manufacturerId || null,
      shippingOptionId: form.shippingOptionId || null,
      projectId: form.projectId || null,
      departmentId: form.departmentId || null,
      deliveryPriorityId: form.deliveryPriorityId || null,
    };
    await saveFlow({
      formName: 'ICM Requisition',
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        if (isNew) {
          await api.post('/api/v1/requisitions', payload);
        } else {
          await api.put(`/api/v1/requisitions/${id}`, payload);
        }
      },
      // The Save SP enforces the legacy required-field cascade and the
      // "Ordersameitem" duplicate guard, and the API returns them as a 400
      // BUSINESS_RULE (or 403 when the user lacks the Write right). Without
      // this the shared flow would say only "Save failed" and never tell the
      // user WHICH field to fix — the exact complaint the middleware's own
      // BUSINESS_RULE branch exists to answer.
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        notify.error(msg ?? 'Save failed.');
      },
      onSaved: () => navigate('/m/icm-requisition'),
    });
  }

  async function handleSubmit() {
    if (!(await confirm({ title: 'Submit Requisition?', message: 'Submit this requisition for review?', confirmLabel: 'Submit' }))) return;
    setSaving(true);
    try {
      await api.post(`/api/v1/requisitions/${id}/submit`, {});
      notify.success('Requisition submitted.');
      navigate('/m/icm-requisition');
    } catch (e) {
      // Submit re-runs the same two guards against the stored row, so show
      // what it said rather than a bare "Failed to submit."
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Failed to submit.');
    } finally {
      setSaving(false);
    }
  }

  // Amber "required yet empty" cue on a required control (BO UI Standard §4,
  // standing rule 12). The yellow editable tint is already on every .sd-input.
  const reqCls = (v: unknown) =>
    'sd-input' + (v === '' || v == null ? ' required-empty' : '');

  if (loading) return <div className="bl-page"><div className="bl-empty">Loading…</div></div>;

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            <span onClick={() => navigate('/m/icm-requisition')} style={{ cursor: 'pointer' }}>Item Requisition</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{isNew ? 'New' : rqId}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-requisitions" title={isNew ? 'New Requisition' : `Requisition — ${rqId}`} loadMs={null} loading={false} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={loadRecord}
            refreshing={loading}
          />
        </div>

        <div className="bl-card" style={{ padding: 20 }}>
          {!isNew && rqId && (
            <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>RQID: {rqId}</span>
              <span className="adg-status-pill">{status}</span>
            </div>
          )}

          {/* The shared detail field layout (ModuleCrudConfig.sideLabels +
              columnMajor, standing rule 12): two columns filled top-to-bottom, a
              fixed left-aligned label column, every control a uniform 26px. This
              was a BARE `sd-grid` until 2026-09-29, which is the shared grid's
              STACKED layout -- captions above their fields, taller rows -- so
              this form did not match Item Initialization or Vendors COA beside
              it (owner request: "fix the layout based on the AI form standard
              and check other forms").
              --sd-rows is the taller column: 11 fields over 2 columns = 6. Too
              small a number spills the overflow into a third implicit column. */}
          <div className="sd-grid sd-grid--colmajor sd-grid--side"
               style={{ ['--sd-rows' as string]: '6' }}>
            <label className="sd-row">
              <span className="sd-label">Item <span className="req">*</span></span>
              <select className={reqCls(form.itemId)} value={form.itemId} onChange={e => set('itemId', e.target.value)}>
                <option value="">— select —</option>
                {items.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row">
              <span className="sd-label">Vendor</span>
              <select className="sd-input" value={form.vendorId} onChange={e => set('vendorId', e.target.value)}>
                <option value="">— select —</option>
                {vendors.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row">
              <span className="sd-label">Manufacturer</span>
              <select className="sd-input" value={form.manufacturerId} onChange={e => set('manufacturerId', e.target.value)}>
                <option value="">— select —</option>
                {manufacturers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row">
              <span className="sd-label">Catalog #</span>
              <input className="sd-input" type="text" value={form.catalog} onChange={e => set('catalog', e.target.value)} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Order Qty <span className="req">*</span></span>
              <input className={reqCls(form.orderQty ? form.orderQty : '')} type="number" min={1} value={form.orderQty} onChange={e => set('orderQty', Number(e.target.value))} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Unit Price</span>
              <input className="sd-input" type="number" min={0} step="0.01" value={form.unitPrice} onChange={e => set('unitPrice', Number(e.target.value))} />
            </label>

            <label className="sd-row">
              <span className="sd-label">Shipping Option</span>
              <select className="sd-input" value={form.shippingOptionId} onChange={e => set('shippingOptionId', e.target.value)}>
                <option value="">— select —</option>
                {shippingOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row">
              <span className="sd-label">Delivery Priority <span className="req">*</span></span>
              <select className={reqCls(form.deliveryPriorityId)} value={form.deliveryPriorityId} onChange={e => set('deliveryPriorityId', e.target.value)}>
                <option value="">— select —</option>
                {deliveryPriorities.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row">
              <span className="sd-label">Department <span className="req">*</span></span>
              <select className={reqCls(form.departmentId)} value={form.departmentId} onChange={e => set('departmentId', e.target.value)}>
                <option value="">— select —</option>
                {departments.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row">
              <span className="sd-label">Project</span>
              <select className="sd-input" value={form.projectId} onChange={e => set('projectId', e.target.value)}>
                <option value="">— select —</option>
                {projects.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>

            <label className="sd-row full">
              <span className="sd-label">Comment</span>
              <textarea className="sd-input" rows={3} value={form.comment} onChange={e => set('comment', e.target.value)} />
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button
              className="ca-btn"
              onClick={handleSave}
              disabled={saving || !canWrite}
              title={canWrite ? undefined : 'You do not have permission to create or change a requisition.'}
            >
              <i className="ti ti-device-floppy" /> Save
            </button>
            {!isNew && (
              <button
                className="ca-btn"
                onClick={handleSubmit}
                disabled={saving || !canWrite}
                title={canWrite ? undefined : 'You do not have permission to create or change a requisition.'}
              >
                <i className="ti ti-send" /> Submit
              </button>
            )}
            <button className="ca-btn ca-btn-ghost" onClick={() => navigate('/m/icm-requisition')}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
